import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Ticket, CheckCircle, Clock, AlertCircle,
  Search, Filter, ChevronLeft, ChevronRight
} from 'lucide-react';
import api from '../../utils/api';
import { exportToExcel, exportToCSV, exportAnalyticsPDF } from '../../utils/exportUtils';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const resolutionTime = (created, resolved) => {
  if (!created || !resolved) return '—';
  const diff = new Date(resolved) - new Date(created);
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return '<1h';
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
};

const STATUS_OPTIONS = ['all', 'open', 'in-progress', 'resolved', 'closed'];
const CATEGORY_OPTIONS = ['all', 'technical', 'account', 'network', 'billing', 'course', 'assessment', 'course & assessment', 'career Direction', 'placement issue', 'certificates & badges issue', 'other'];
const PRIORITY_OPTIONS = ['all', 'low', 'medium', 'high', 'urgent'];
const PER_PAGE = 15;

const statusStyle = (status, isDark) => {
  const map = {
    open: isDark ? 'bg-rose-950/40 text-rose-400' : 'bg-rose-50 text-rose-700',
    'in-progress': isDark ? 'bg-amber-950/40 text-amber-400' : 'bg-amber-50 text-amber-700',
    resolved: isDark ? 'bg-emerald-950/40 text-emerald-400' : 'bg-emerald-50 text-emerald-700',
    closed: isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600',
  };
  return map[status] || (isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600');
};

const priorityStyle = (priority, isDark) => {
  const map = {
    urgent: isDark ? 'text-rose-400' : 'text-rose-600',
    high: isDark ? 'text-orange-400' : 'text-orange-600',
    medium: isDark ? 'text-amber-400' : 'text-amber-600',
    low: isDark ? 'text-emerald-400' : 'text-emerald-600',
  };
  return map[priority] || (isDark ? 'text-gray-400' : 'text-gray-500');
};

// ─── Filter Select ────────────────────────────────────────────────────────────
const FilterSelect = ({ label, value, options, onChange, isDark }) => (
  <div className="flex flex-col gap-1 min-w-[130px]">
    <span className={`text-[10px] font-bold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{label}</span>
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className={`text-xs font-semibold rounded-lg px-2.5 py-1.5 border focus:outline-none cursor-pointer ${
        isDark ? 'bg-[#0F225C] border-[#1a3884]/60 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
      }`}
    >
      {options.map(o => (
        <option key={o} value={o} className="capitalize">{o === 'all' ? `All ${label}s` : o}</option>
      ))}
    </select>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const SupportAnalytics = ({ isDark }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [statusF, setStatusF] = useState('all');
  const [categoryF, setCategoryF] = useState('all');
  const [priorityF, setPriorityF] = useState('all');
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/tickets/admin/all');
      if (res.data.success) setTickets(res.data.data || []);
    } catch (e) {
      console.error('Support tickets fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  // ─── Derived KPIs ─────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const total = tickets.length;
    const open = tickets.filter(t => t.status === 'open').length;
    const inProg = tickets.filter(t => t.status === 'in-progress').length;
    const resolved = tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length;
    const escalated = tickets.filter(t => t.priority === 'urgent' || t.priority === 'high').length;
    const resolution = total > 0 ? Math.round((resolved / total) * 100) : 0;
    return { total, open, inProg, resolved, escalated, resolution };
  }, [tickets]);

  // ─── Filtered + Paginated Data ────────────────────────────────────────────
  const filtered = useMemo(() => {
    return tickets.filter(t => {
      if (statusF !== 'all' && t.status !== statusF) return false;
      if (categoryF !== 'all' && t.category !== categoryF) return false;
      if (priorityF !== 'all' && t.priority !== priorityF) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const subject = (t.subject || t.title || '').toLowerCase();
        const ticketId = (t.ticketId || '').toLowerCase();
        const raisedBy = (t.userId?.fullName || t.userId?.email || '').toLowerCase();
        const assignedTo = (t.assignedTo?.fullName || t.assignedTo?.name || '').toLowerCase();
        if (!subject.includes(q) && !ticketId.includes(q) && !raisedBy.includes(q) && !assignedTo.includes(q)) return false;
      }
      return true;
    });
  }, [tickets, statusF, categoryF, priorityF, search]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // Reset page when filters change
  useEffect(() => setPage(1), [search, statusF, categoryF, priorityF]);

  // ─── Export Handlers ──────────────────────────────────────────────────────
  const exportColumns = [
    { header: 'Ticket ID', accessor: 'ticketId' },
    { header: 'Subject', accessor: 'subject' },
    { header: 'Category', accessor: 'category' },
    { header: 'Assigned To', accessor: 'assignedTo' },
    { header: 'Status', accessor: 'status' },
    { header: 'Priority', accessor: 'priority' },
    { header: 'Resolution Time', accessor: 'resolutionTime' },
    { header: 'Created', accessor: 'created' },
    { header: 'Closed', accessor: 'closed' },
  ];

  const buildExportData = (data) => data.map(t => ({
    ticketId: t.ticketId || '—',
    subject: t.subject || t.title || '—',
    category: t.category || '—',
    assignedTo: t.assignedTo?.fullName || t.assignedTo?.name || '—',
    status: t.status || '—',
    priority: t.priority || '—',
    resolutionTime: resolutionTime(t.createdAt, t.resolvedAt || t.closedAt),
    created: fmtDate(t.createdAt),
    closed: fmtDate(t.closedAt || t.resolvedAt),
  }));

  const handleExportCSV = () => {
    exportToCSV(buildExportData(filtered), exportColumns, 'Support_Tickets');
  };
  const handleExportExcel = () => {
    exportToExcel(buildExportData(filtered), exportColumns, 'Support_Tickets');
  };
  const handleExportPDF = () => {
    exportAnalyticsPDF({ tickets: buildExportData(filtered) }, 'Support Tickets');
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="relative w-12 h-12">
          <div className={`absolute inset-0 border-4 rounded-full ${isDark ? 'border-[#1a3884]/40' : 'border-gray-200'}`} />
          <div className="absolute inset-0 border-4 border-[#284BB3] rounded-full border-t-transparent animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── KPI Row ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Total Tickets', value: kpis.total, icon: Ticket, color: isDark ? '#60A5FA' : '#284BB3' },
          { label: 'Open', value: kpis.open, icon: AlertCircle, color: isDark ? '#F87171' : '#EF4444' },
          { label: 'In Progress', value: kpis.inProg, icon: Clock, color: isDark ? '#FBBF24' : '#F59E0B' },
          { label: 'Resolved', value: kpis.resolved, icon: CheckCircle, color: isDark ? '#34D399' : '#10B981' },
          { label: 'Escalated', value: kpis.escalated, icon: AlertCircle, color: isDark ? '#F87171' : '#EF4444' },
          { label: 'Resolution Rate', value: `${kpis.resolution}%`, icon: CheckCircle, color: isDark ? '#A78BFA' : '#7C3AED', description: 'Resolved + Closed / Total' },
        ].map(item => (
          <motion.div
            key={item.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-xl p-4 border flex items-center gap-3 ${isDark ? 'bg-[#002147] border-[#1a3884]/50' : 'bg-white border-gray-200 shadow-sm'}`}
          >
            <item.icon size={18} style={{ color: item.color }} className="flex-shrink-0" />
            <div>
              <p className="text-xl font-black" style={{ color: item.color }}>{item.value}</p>
              <p className={`text-[10px] font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{item.label}</p>
              {item.description && (
                <p 
                  className={`text-[9px] mt-0.5 font-medium leading-tight ${isDark ? 'text-slate-400' : 'text-gray-400'}`} 
                  title="Resolution Rate = (Resolved + Closed) / Total tickets"
                >
                  {item.description}
                </p>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* ── Ticket Table Card ── */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className={`rounded-2xl border ${isDark ? 'bg-[#002147] border-[#1a3884]/50 shadow-lg' : 'bg-white border-gray-200 shadow-sm'}`}
      >
        {/* Table header */}
        <div className="p-5 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Support Ticket Log</h3>
            <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              {filtered.length} tickets {filtered.length !== tickets.length ? `(filtered from ${tickets.length})` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Search */}
            <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${isDark ? 'bg-[#0F225C] border-[#1a3884]/60' : 'bg-gray-50 border-gray-300'}`}>
              <Search size={13} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search tickets…"
                className={`text-xs bg-transparent focus:outline-none w-36 ${isDark ? 'text-gray-200 placeholder-gray-600' : 'text-gray-700 placeholder-gray-400'}`}
              />
            </div>
            {/* Filter toggle */}
            <button
              onClick={() => setShowFilters(f => !f)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-bold transition-all ${
                showFilters
                  ? isDark ? 'bg-[#1A3884]/50 border-[#284BB3] text-white' : 'bg-blue-50 border-blue-300 text-blue-700'
                  : isDark ? 'border-[#1a3884]/50 text-gray-300 hover:bg-[#1a3884]/20' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Filter size={12} />
              Filters {showFilters && (statusF !== 'all' || categoryF !== 'all' || priorityF !== 'all') && (
                <span className={`ml-0.5 px-1 rounded-full text-[9px] font-black ${isDark ? 'bg-blue-500 text-white' : 'bg-blue-600 text-white'}`}>●</span>
              )}
            </button>
            {/* Export dropdown */}
            <div className="flex items-center gap-1">
              <button onClick={handleExportCSV} className={`px-3 py-2 rounded-xl border text-xs font-bold transition-all ${isDark ? 'border-[#1a3884]/50 text-gray-300 hover:bg-[#1a3884]/20' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>CSV</button>
              <button onClick={handleExportExcel} className={`px-3 py-2 rounded-xl border text-xs font-bold transition-all ${isDark ? 'border-[#1a3884]/50 text-gray-300 hover:bg-[#1a3884]/20' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>Excel</button>
              <button onClick={handleExportPDF} className={`px-3 py-2 rounded-xl border text-xs font-bold transition-all ${isDark ? 'border-[#1a3884]/50 text-gray-300 hover:bg-[#1a3884]/20' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>PDF</button>
            </div>
          </div>
        </div>

        {/* Filters row */}
        {showFilters && (
          <div className={`px-5 pb-4 flex flex-wrap gap-4 border-t ${isDark ? 'border-[#1a3884]/30' : 'border-gray-100'}`}>
            <div className="pt-4">
              <FilterSelect label="Status" value={statusF} options={STATUS_OPTIONS} onChange={setStatusF} isDark={isDark} />
            </div>
            <div className="pt-4">
              <FilterSelect label="Category" value={categoryF} options={CATEGORY_OPTIONS} onChange={setCategoryF} isDark={isDark} />
            </div>
            <div className="pt-4">
              <FilterSelect label="Priority" value={priorityF} options={PRIORITY_OPTIONS} onChange={setPriorityF} isDark={isDark} />
            </div>
            {(statusF !== 'all' || categoryF !== 'all' || priorityF !== 'all') && (
              <div className="pt-4 flex items-end">
                <button
                  onClick={() => { setStatusF('all'); setCategoryF('all'); setPriorityF('all'); }}
                  className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-all ${isDark ? 'text-rose-400 hover:bg-rose-950/30' : 'text-rose-600 hover:bg-rose-50'}`}
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={`border-t border-b ${isDark ? 'border-[#1a3884]/30 bg-[#0F225C]/30' : 'border-gray-200 bg-gray-50'}`}>
                {['Ticket ID', 'Subject', 'Category', 'Assigned To', 'Status', 'Priority', 'Resolution Time', 'Created', 'Closed'].map(h => (
                  <th key={h} className={`px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-[#1a3884]/20' : 'divide-gray-100'}`}>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={9} className={`px-4 py-16 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    <Ticket className="w-10 h-10 mx-auto mb-3 opacity-30" />
                    <p className="text-sm font-medium">No tickets match the current filters</p>
                  </td>
                </tr>
              ) : (
                paginated.map((t, i) => (
                  <tr
                    key={t._id || i}
                    className={`transition-colors ${isDark ? 'hover:bg-[#0F225C]/30' : 'hover:bg-gray-50'}`}
                  >
                    <td className={`px-4 py-2.5 font-mono text-xs ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                      {t.ticketId || t._id?.slice(-6) || `#${i + 1}`}
                    </td>
                    <td className={`px-4 py-2.5 max-w-[200px] ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                      <p className="text-xs font-semibold truncate">{t.subject || t.title || '—'}</p>
                    </td>
                    <td className={`px-4 py-2.5 text-xs capitalize ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {t.category || '—'}
                    </td>
                    <td className={`px-4 py-2.5 text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {t.assignedTo?.fullName || t.assignedTo?.name || '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${statusStyle(t.status, isDark)}`}>
                        {t.status || 'open'}
                      </span>
                    </td>
                    <td className={`px-4 py-2.5 text-xs font-semibold capitalize ${priorityStyle(t.priority, isDark)}`}>
                      {t.priority || '—'}
                    </td>
                    <td className={`px-4 py-2.5 text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {resolutionTime(t.createdAt, t.resolvedAt || t.closedAt)}
                    </td>
                    <td className={`px-4 py-2.5 text-xs whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {fmtDate(t.createdAt)}
                    </td>
                    <td className={`px-4 py-2.5 text-xs whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {fmtDate(t.closedAt || t.resolvedAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={`flex items-center justify-between px-5 py-4 border-t ${isDark ? 'border-[#1a3884]/30' : 'border-gray-200'}`}>
            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} of {filtered.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className={`p-1.5 rounded-lg border transition-all disabled:opacity-40 ${isDark ? 'border-[#1a3884]/50 text-gray-300 hover:bg-[#1a3884]/20' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
              >
                <ChevronLeft size={14} />
              </button>
              <span className={`text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className={`p-1.5 rounded-lg border transition-all disabled:opacity-40 ${isDark ? 'border-[#1a3884]/50 text-gray-300 hover:bg-[#1a3884]/20' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default SupportAnalytics;
